import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';

const C = {
  card: '#1C1C28', border: '#2A2A3A', surface: '#16161F',
  accent: '#E8A2B8', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5', purple: '#B39DDB',
};

const SYMPTOM_LABELS = {
  skinflare: 'skin flares',
  cramps: 'cramps',
  mood_low: 'low mood',
  mood_high: 'high energy',
  bloat: 'bloating',
  headache: 'headaches',
  spotting: 'spotting',
  sex: 'changes in libido',
};

// Medication name → canonical key (must match medicationInsights.js keys)
const MED_ALIASES = {
  'combined pill': ['microgynon', 'cilest', 'yasmin', 'rigevidon', 'combined', 'coc'],
  'progestogen-only pill': ['mini pill', 'cerelle', 'cerazette', 'desogestrel', 'noriday'],
  'hormonal coil': ['mirena', 'kyleena', 'jaydess', 'coil', 'iud'],
  'implant': ['nexplanon', 'implanon'],
  'hrt': ['hrt', 'hormone replacement', 'oestrogen', 'estrogen', 'estradiol', 'utrogestan', 'elleste', 'evorel'],
  'metformin': ['metformin', 'glucophage'],
  'spironolactone': ['spironolactone', 'spiro', 'aldactone'],
  'ssri': ['sertraline', 'fluoxetine', 'citalopram', 'escitalopram', 'prozac', 'venlafaxine', 'duloxetine', 'ssri', 'snri', 'antidepressant'],
  'isotretinoin': ['roaccutane', 'accutane', 'isotretinoin', 'tretinoin'],
  'ibuprofen': ['ibuprofen', 'nurofen', 'naproxen', 'mefenamic', 'ponstan', 'nsaid'],
  'iron': ['iron', 'ferrous', 'feroglobin', 'spatone'],
  'vitamin d': ['vitamin d', 'vit d', 'cholecalciferol'],
  'magnesium': ['magnesium'],
};

function resolveMed(name) {
  const lower = name.toLowerCase();
  for (const [key, aliases] of Object.entries(MED_ALIASES)) {
    if (lower.includes(key) || aliases.some(a => lower.includes(a))) return key;
  }
  return null;
}

const articles = [
  // CYCLES
  {
    id: 'cycle-phases',
    topic: 'cycles',
    title: 'The Four Phases of Your Menstrual Cycle',
    source: 'NHS',
    tag: 'Foundational',
    link: 'https://www.nhs.uk/conditions/periods/',
    summary: 'An overview of the menstrual, follicular, ovulatory, and luteal phases — what\'s happening hormonally and physically in each.',
    relevantTo: { symptoms: ['cramps', 'spotting', 'mood_low', 'mood_high', 'bloat'], meds: [], phases: ['menstrual', 'follicular', 'ovulation', 'luteal'] },
  },
  {
    id: 'irregular-periods',
    topic: 'cycles',
    title: 'Irregular Periods — Causes and When to Seek Help',
    source: 'NHS',
    tag: 'Conditions',
    link: 'https://www.nhs.uk/conditions/irregular-periods/',
    summary: 'Covers what counts as irregular, common causes including stress and hormonal imbalance, and when a GP referral is warranted.',
    relevantTo: { symptoms: ['spotting', 'cramps'], meds: ['combined pill', 'progestogen-only pill', 'hormonal coil', 'implant'], phases: [] },
  },
  {
    id: 'heavy-periods',
    topic: 'cycles',
    title: 'Heavy Periods (Menorrhagia)',
    source: 'NHS',
    tag: 'Conditions',
    link: 'https://www.nhs.uk/conditions/heavy-periods/',
    summary: 'What qualifies as heavy bleeding, potential causes including fibroids and endometriosis, and treatment options including hormonal and non-hormonal approaches.',
    relevantTo: { symptoms: ['cramps', 'spotting'], meds: ['iron', 'ibuprofen', 'combined pill', 'hormonal coil'], phases: ['menstrual'] },
  },
  {
    id: 'pms',
    topic: 'mood',
    title: 'Premenstrual Syndrome (PMS)',
    source: 'NHS',
    tag: 'Mental health',
    link: 'https://www.nhs.uk/conditions/premenstrual-syndrome/',
    summary: 'Symptoms, causes, and management of PMS — including lifestyle changes, CBT, and when medication may help.',
    relevantTo: { symptoms: ['mood_low', 'bloat', 'cramps', 'headache'], meds: ['combined pill', 'ssri', 'magnesium'], phases: ['luteal'] },
  },
  {
    id: 'pmdd',
    topic: 'mood',
    title: 'PMDD: Premenstrual Dysphoric Disorder',
    source: 'RCOG',
    tag: 'Condition',
    link: 'https://www.rcog.org.uk/for-the-public/browse-our-patient-information/premenstrual-syndrome-pms/',
    summary: 'A severe form of PMS involving significant mood disruption in the luteal phase. Covers diagnosis criteria, SSRI treatment, and hormonal approaches.',
    relevantTo: { symptoms: ['mood_low'], meds: ['ssri', 'combined pill'], phases: ['luteal'] },
  },
  {
    id: 'mental-health-cycle',
    topic: 'mood',
    title: 'How Your Menstrual Cycle Affects Mental Health',
    source: 'Mind',
    tag: 'Mental health',
    link: 'https://www.mind.org.uk/information-support/types-of-mental-health-problems/premenstrual-dysphoric-disorder-pmdd/',
    summary: 'Explores the hormonal basis of mood changes across the cycle, practical self-management strategies, and when to seek professional support.',
    relevantTo: { symptoms: ['mood_low', 'mood_high'], meds: ['ssri', 'combined pill', 'hrt'], phases: ['luteal', 'menstrual'] },
  },
  {
    id: 'hormonal-acne',
    topic: 'skin',
    title: 'Hormonal Acne and the Menstrual Cycle',
    source: 'British Association of Dermatologists',
    tag: 'Dermatology',
    link: 'https://www.bad.org.uk/for-the-public/patient-information-leaflets/acne/',
    summary: 'Why breakouts cluster in the luteal phase, how androgens drive sebum production, and evidence-based treatments including topical retinoids and hormonal options.',
    relevantTo: { symptoms: ['skinflare'], meds: ['combined pill', 'spironolactone', 'isotretinoin', 'progestogen-only pill', 'implant'], phases: ['luteal'] },
  },
  {
    id: 'skin-barrier',
    topic: 'skin',
    title: 'Skin Barrier Function and Hormonal Changes',
    source: 'Wellcome',
    tag: 'Research',
    link: 'https://wellcome.org/news/skin-microbiome',
    summary: 'How oestrogen and progesterone fluctuations affect skin hydration, sensitivity, and barrier integrity across the cycle.',
    relevantTo: { symptoms: ['skinflare'], meds: ['hrt', 'combined pill'], phases: ['menstrual', 'luteal'] },
  },
  {
    id: 'pcos',
    topic: 'conditions',
    title: 'Polycystic Ovary Syndrome (PCOS)',
    source: 'NHS',
    tag: 'Hormonal',
    link: 'https://www.nhs.uk/conditions/polycystic-ovary-syndrome-pcos/',
    summary: 'Symptoms, diagnosis, and management of PCOS — including effects on periods, skin, weight, and fertility, and the role of metformin and the combined pill.',
    relevantTo: { symptoms: ['skinflare', 'mood_low', 'spotting'], meds: ['metformin', 'combined pill', 'spironolactone'], phases: [] },
  },
  {
    id: 'endometriosis',
    topic: 'conditions',
    title: 'Endometriosis: Symptoms, Diagnosis and Support',
    source: 'Endometriosis UK',
    tag: 'Condition',
    link: 'https://www.endometriosis-uk.org/understanding-endometriosis',
    summary: 'What endometriosis is, why it takes an average of 8 years to diagnose, key symptoms to track, and treatment options.',
    relevantTo: { symptoms: ['cramps', 'bloat', 'mood_low', 'spotting'], meds: ['combined pill', 'hormonal coil', 'ibuprofen'], phases: ['menstrual'] },
  },
  {
    id: 'contraception-guide',
    topic: 'conditions',
    title: 'Your Contraception Guide',
    source: 'NHS',
    tag: 'Contraception',
    link: 'https://www.nhs.uk/conditions/contraception/',
    summary: 'Comprehensive overview of all contraceptive options, how they work, and their effects on periods, mood, and skin.',
    relevantTo: { symptoms: ['spotting', 'mood_low', 'skinflare'], meds: ['combined pill', 'progestogen-only pill', 'hormonal coil', 'implant'], phases: [] },
  },
  {
    id: 'iron-deficiency',
    topic: 'conditions',
    title: 'Iron Deficiency Anaemia',
    source: 'NHS',
    tag: 'Nutrition',
    link: 'https://www.nhs.uk/conditions/iron-deficiency-anaemia/',
    summary: 'How heavy periods lead to iron deficiency, symptoms to watch for, dietary sources, and when supplementation is needed.',
    relevantTo: { symptoms: ['mood_low', 'cramps'], meds: ['iron'], phases: ['menstrual'] },
  },
];

const topics = [
  { id: 'all', label: 'All' },
  { id: 'cycles', label: 'Cycles' },
  { id: 'mood', label: 'Mood' },
  { id: 'skin', label: 'Skin' },
  { id: 'conditions', label: 'Conditions' },
];

const sources = ['NHS', 'NICE', 'RCOG', 'Mind', 'Endometriosis UK', 'Wellcome', 'BAD'];

function buildRelevanceReason(article, userSymptoms, userMedKeys) {
  const reasons = [];

  const matchedSymptoms = article.relevantTo.symptoms
    .filter(s => userSymptoms.has(s))
    .map(s => SYMPTOM_LABELS[s])
    .filter(Boolean);

  const matchedMeds = article.relevantTo.meds
    .filter(m => userMedKeys.has(m));

  if (matchedSymptoms.length > 0) {
    reasons.push(`you've logged ${matchedSymptoms.slice(0, 2).join(' and ')}`);
  }
  if (matchedMeds.length > 0) {
    reasons.push(`you take ${matchedMeds[0]}`);
  }

  return reasons.length > 0
    ? `Relevant to you because ${reasons.join(', and ')}.`
    : null;
}

export default function Discover() {
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [userSymptoms, setUserSymptoms] = useState(new Set());
  const [userMedKeys, setUserMedKeys] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API_URL}/api/logs`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/medications`, { headers }).catch(() => ({ data: [] })),
    ]).then(([logsRes, medsRes]) => {
      setUserSymptoms(new Set(logsRes.data.map(l => l.category)));
      setUserMedKeys(new Set(medsRes.data.map(m => resolveMed(m.name)).filter(Boolean)));
      setLoaded(true);
    });
  }, []);

  const filtered = selectedTopic === 'all'
    ? articles
    : articles.filter(a => a.topic === selectedTopic);

  // Sort: personalised matches first
  const sorted = [...filtered].sort((a, b) => {
    const aMatch = buildRelevanceReason(a, userSymptoms, userMedKeys) ? 1 : 0;
    const bMatch = buildRelevanceReason(b, userSymptoms, userMedKeys) ? 1 : 0;
    return bMatch - aMatch;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", color: C.text, fontSize: 22, margin: '0 0 4px' }}>Discover</h2>
        <p style={{ fontSize: 12, color: C.muted }}>Evidence-based articles, personalised to your health data.</p>
      </div>

      {/* Trusted sources */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {sources.map(s => (
          <div key={s} style={{ background: C.card, borderRadius: 20, padding: '4px 12px', fontSize: 11, color: C.teal, border: `1px solid ${C.teal}44` }}>{s}</div>
        ))}
      </div>

      {/* Topic pills */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
        {topics.map(t => (
          <button key={t.id} onClick={() => setSelectedTopic(t.id)}
            style={{
              flexShrink: 0, padding: '6px 14px',
              background: selectedTopic === t.id ? `${C.teal}22` : C.card,
              border: `1.5px solid ${selectedTopic === t.id ? C.teal : C.border}`,
              color: selectedTopic === t.id ? C.teal : C.muted,
              borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 500,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Articles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sorted.map(article => {
          const reason = loaded ? buildRelevanceReason(article, userSymptoms, userMedKeys) : null;
          return (
            <a key={article.id} href={article.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <div style={{
                background: C.card, borderRadius: 16, padding: 16,
                border: `1px solid ${reason ? C.accent + '55' : C.border}`,
                transition: 'border-color 0.15s',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 4 }}>{article.title}</div>
                    <div style={{ fontSize: 11, color: C.teal, fontFamily: 'monospace' }}>{article.source}</div>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0 }}>{article.tag}</div>
                </div>

                {/* Summary */}
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: reason ? 10 : 0 }}>
                  {article.summary}
                </div>

                {/* Personalised relevance */}
                {reason && (
                  <div style={{
                    marginTop: 10, padding: '8px 10px',
                    background: `${C.accent}11`, borderRadius: 8,
                    borderLeft: `3px solid ${C.accent}`,
                    fontSize: 11, color: C.accent, lineHeight: 1.5,
                  }}>
                    ✦ {reason}
                  </div>
                )}
              </div>
            </a>
          );
        })}
      </div>

      {/* Shield */}
      <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.danger, fontFamily: 'monospace', marginBottom: 6 }}>⚠ MISINFORMATION SHIELD</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
          Sylva only surfaces content from registered health organisations and peer-reviewed sources. Content from social media, unverified blogs, or anonymous sources is never shown.
        </div>
      </div>

      <div style={{ background: `linear-gradient(135deg,#1A1A28,#1C1C2A)`, borderRadius: 16, padding: 16, border: `1px solid ${C.teal}33` }}>
        <div style={{ fontSize: 10, color: C.teal, fontFamily: 'monospace', marginBottom: 6 }}>POWERED BY</div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 4 }}>Dimensions (Digital Science)</div>
        <div style={{ fontSize: 12, color: C.muted }}>A continuously updated academic database. Future versions will surface live peer-reviewed research contextualised to your health data.</div>
      </div>
    </div>
  );
}
